import { useAuth } from '@/contexts/auth-context';
import { useMobileSubscription } from '@/hooks/use-mobile-subscription';
import { SubscriptionTest } from '@/components/subscription-test';
import { SubscriptionManagement } from '@/components/subscription-management';
import { SubscriptionDialog } from '@/components/subscription-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Smartphone, Monitor, Settings, TestTube } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

export default function SubscriptionTestPage() {
  const { user, isSubscribed } = useAuth();
  const { isInitialized, isLoading } = useMobileSubscription(user?.uid);

  const platform = Capacitor.getPlatform();
  const isNative = Capacitor.isNativePlatform();

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Subscription Testing</h1>
        <p className="text-muted-foreground">
          Test subscription functionality across different platforms
        </p>
      </div>

      {/* Platform Info */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isNative ? <Smartphone className="h-5 w-5" /> : <Monitor className="h-5 w-5" />}
            Platform Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <strong>Platform:</strong> {platform}
            </div>
            <div>
              <strong>Native:</strong> {isNative ? 'Yes' : 'No'}
            </div>
            <div>
              <strong>User:</strong> {user ? user.email : 'Not logged in'}
            </div>
            <div>
              <strong>Subscribed:</strong> {isSubscribed ? 'Yes' : 'No'}
            </div>
            <div>
              <strong>Service Initialized:</strong> {isInitialized ? 'Yes' : 'No'}
            </div>
            <div>
              <strong>Loading:</strong> {isLoading ? 'Yes' : 'No'}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Testing Tabs */}
      <Tabs defaultValue="test" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="test" className="flex items-center gap-2">
            <TestTube className="h-4 w-4" />
            Test
          </TabsTrigger>
          <TabsTrigger value="subscribe" className="flex items-center gap-2">
            <Smartphone className="h-4 w-4" />
            Subscribe
          </TabsTrigger>
          <TabsTrigger value="manage" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Manage
          </TabsTrigger>
          <TabsTrigger value="info" className="flex items-center gap-2">
            <Monitor className="h-4 w-4" />
            Info
          </TabsTrigger>
        </TabsList>

        {/* Test Tab */}
        <TabsContent value="test" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Subscription Test Component</CardTitle>
              <CardDescription>
                Debug subscription functionality and check status
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SubscriptionTest />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscribe Tab */}
        <TabsContent value="subscribe" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Subscribe to LullabAI</CardTitle>
              <CardDescription>
                Test the subscription purchase flow
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isSubscribed ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {isNative 
                      ? 'Test the mobile subscription purchase flow. This will open the native store purchase dialog.'
                      : 'On web, subscription functionality is limited. Please test on mobile devices for full functionality.'
                    }
                  </p>
                  
                  <SubscriptionDialog 
                    trigger={
                      <Button className="w-full" size="lg">
                        Subscribe Now - £8.99/month
                      </Button>
                    }
                  />

                  {!isNative && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        <strong>Note:</strong> Full subscription functionality is only available on mobile devices. 
                        Please test on iOS or Android for complete functionality.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2">Already Subscribed</h4>
                  <p className="text-sm text-green-700">
                    You already have an active subscription. Use the "Manage" tab to view details or cancel.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Manage Tab */}
        <TabsContent value="manage" className="space-y-4">
          {isSubscribed ? (
            <SubscriptionManagement />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>No Active Subscription</CardTitle>
                <CardDescription>
                  You don't have an active subscription to manage
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Subscribe to LullabAI to access subscription management features.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Info Tab */}
        <TabsContent value="info" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Testing Information</CardTitle>
              <CardDescription>
                Platform-specific testing instructions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isNative ? (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">
                      {platform === 'ios' ? 'iOS Testing' : 'Android Testing'}
                    </h4>
                    <div className="text-sm text-blue-700 space-y-2">
                      <p><strong>Platform:</strong> {platform}</p>
                      <p><strong>Testing Mode:</strong> {platform === 'ios' ? 'Sandbox' : 'Internal Testing'}</p>
                      <p><strong>Account Required:</strong> {platform === 'ios' ? 'Apple ID Sandbox Account' : 'Google Play Test Account'}</p>
                    </div>
                  </div>

                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-2">Testing Steps</h4>
                    <ol className="text-sm text-green-700 space-y-1">
                      <li>1. Ensure you're signed in to a test account</li>
                      <li>2. Test subscription purchase</li>
                      <li>3. Verify subscription status updates</li>
                      <li>4. Test restore purchases</li>
                      <li>5. Test cancellation flow</li>
                    </ol>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="font-semibold text-yellow-800 mb-2">Web Platform Limitations</h4>
                    <p className="text-sm text-yellow-700">
                      Subscription functionality is limited on web platforms. For full testing, please use:
                    </p>
                    <ul className="text-sm text-yellow-700 mt-2 space-y-1">
                      <li>• iOS device with TestFlight</li>
                      <li>• Android device with internal testing</li>
                      <li>• Mobile browser (limited functionality)</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">Web Testing</h4>
                    <p className="text-sm text-blue-700">
                      On web, you can test the UI components and basic functionality, but actual purchases require mobile platforms.
                    </p>
                  </div>
                </div>
              )}

              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Debug Information</h4>
                <div className="text-sm text-gray-700 space-y-1">
                  <p><strong>User ID:</strong> {user?.uid || 'Not logged in'}</p>
                  <p><strong>Email:</strong> {user?.email || 'Not logged in'}</p>
                  <p><strong>Subscription Status:</strong> {isSubscribed ? 'Active' : 'Inactive'}</p>
                  <p><strong>Service Status:</strong> {isInitialized ? 'Initialized' : 'Not initialized'}</p>
                  <p><strong>Loading:</strong> {isLoading ? 'Yes' : 'No'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
